import Phaser from 'phaser';
import { Entity } from '../entities/base/Entity';
import { EntityFactory } from './EntityFactory';
import { InstanceData } from './DataManagerEntity';

export interface SpawnConfig {
  /** Margin around viewport for activating entities (default: 200) */
  cullMargin: number;
  /** Margin around viewport for spawning entities (default: 600) */
  spawnMargin: number;
  /** Margin around viewport for destroying entities (default: 1000) */
  despawnMargin: number;
  /** Velocity multiplier for dynamic buffer (default: 0.5) */
  velocityBufferMultiplier: number;
  /** Maximum additional buffer from velocity (default: 400) */
  maxVelocityBuffer: number;
}

/**
 * Dynamic bounds calculated with velocity-based buffer
 */
export interface DynamicBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface SpawnerData {
  /** X position that triggers this spawner */
  triggerX: number;
  /** Entity ID to spawn */
  entityId: string;
  /** Number of entities to spawn */
  count: number;
  /** Time between spawns in ms */
  interval: number;
  /** Area where entities spawn */
  spawnArea: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface BossData {
  /** X position that triggers boss spawn */
  triggerX: number;
  /** Entity ID to use */
  entityId: string;
  /** Spawn position */
  x: number;
  y: number;
}

export interface LevelSpawnData {
  /** Static entity instances */
  entities?: InstanceData[];
  /** Dynamic spawners */
  spawners?: SpawnerData[];
  /** Boss triggers */
  bosses?: BossData[];
}

interface PendingSpawn {
  type: 'static' | 'boss';
  data: InstanceData | BossData;
  x: number;
}

interface ActiveSpawner extends SpawnerData {
  triggered: boolean;
  active: boolean;
  lastSpawnTime: number;
  spawnCount: number;
}

/**
 * RuntimeManagerSpawn - Optimized spawn/despawn system
 *
 * Features:
 * - Lazy spawning: Only creates entities when camera approaches
 * - Auto despawn: Destroys entities when camera moves away
 * - Culling: Deactivates entities outside viewport (no update calls)
 * - Dynamic spawners: Timed wave spawning
 * - Boss triggers: Special spawn handling for bosses
 */
export class RuntimeManagerSpawn {
  private scene: Phaser.Scene;
  private factory: EntityFactory;

  // Configuration
  private config: SpawnConfig = {
    cullMargin: 200,
    spawnMargin: 600,
    despawnMargin: 1000,
    velocityBufferMultiplier: 0.5,
    maxVelocityBuffer: 400,
  };

  // State
  private pendingSpawns: PendingSpawn[] = [];
  private activeEntities: Set<Entity> = new Set();
  private spawnIndex: number = 0;

  // Dynamic spawners
  private spawners: ActiveSpawner[] = [];
  private spawnerIndex: number = 0;

  // Debug
  private debugEnabled: boolean = false;
  private entityIdCounter: number = 0;

  constructor(scene: Phaser.Scene, factory: EntityFactory, config?: Partial<SpawnConfig>) {
    this.scene = scene;
    this.factory = factory;

    if (config) {
      this.config = { ...this.config, ...config };
    }
  }

  /**
   * Load level spawn data
   */
  loadLevel(levelData: LevelSpawnData): void {
    this.clear();

    // Load static entities
    for (const instance of levelData.entities || []) {
      this.pendingSpawns.push({
        type: 'static',
        data: instance,
        x: instance.x,
      });
    }

    // Load spawners
    for (const spawner of levelData.spawners || []) {
      this.spawners.push({
        ...spawner,
        triggered: false,
        active: false,
        lastSpawnTime: 0,
        spawnCount: 0,
      });
    }

    // Load boss triggers
    for (const boss of levelData.bosses || []) {
      this.pendingSpawns.push({
        type: 'boss',
        data: boss,
        x: boss.triggerX,
      });
    }

    // Sort by X position for efficient processing
    this.pendingSpawns.sort((a, b) => a.x - b.x);
    this.spawners.sort((a, b) => a.triggerX - b.triggerX);

    this.spawnIndex = 0;
    this.spawnerIndex = 0;

    // if (this.debugEnabled) {
    //   console.log(`RuntimeManagerSpawn: Loaded ${this.pendingSpawns.length} pending spawns, ${this.spawners.length} spawners`);
    // }
  }

  /**
   * Calculate dynamic bounds with velocity-based buffer expansion
   * The buffer expands in the direction of movement for smoother spawning
   */
  private calculateBounds(
    camera: Phaser.Cameras.Scene2D.Camera,
    margin: number,
    velocity?: Phaser.Math.Vector2
  ): DynamicBounds {
    let extraLeft = 0;
    let extraRight = 0;
    let extraTop = 0;
    let extraBottom = 0;

    // Dynamic buffer based on velocity direction
    if (velocity) {
      const mult = this.config.velocityBufferMultiplier;
      const max = this.config.maxVelocityBuffer;

      // Expand buffer in movement direction
      if (velocity.x > 0) {
        extraRight = Math.min(velocity.x * mult, max);
      } else if (velocity.x < 0) {
        extraLeft = Math.min(-velocity.x * mult, max);
      }

      if (velocity.y > 0) {
        extraBottom = Math.min(velocity.y * mult, max);
      } else if (velocity.y < 0) {
        extraTop = Math.min(-velocity.y * mult, max);
      }
    }

    return {
      left: camera.scrollX - margin - extraLeft,
      right: camera.scrollX + camera.width + margin + extraRight,
      top: camera.scrollY - margin - extraTop,
      bottom: camera.scrollY + camera.height + margin + extraBottom,
    };
  }

  /**
   * Check if a point is within bounds
   */
  private isInBounds(x: number, y: number, bounds: DynamicBounds): boolean {
    return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
  }

  /**
   * Update spawn system - call every frame
   * @param time - Current game time
   * @param _delta - Delta time since last frame
   * @param playerVelocity - Optional player velocity for dynamic buffer
   */
  update(time: number, _delta: number, playerVelocity?: Phaser.Math.Vector2): void {
    const camera = this.scene.cameras.main;

    // Calculate bounds for each zone (with velocity-based expansion)
    const cullBounds = this.calculateBounds(camera, this.config.cullMargin, playerVelocity);
    const spawnBounds = this.calculateBounds(camera, this.config.spawnMargin, playerVelocity);
    const despawnBounds = this.calculateBounds(camera, this.config.despawnMargin);

    // 1. Spawn pending entities (multidirectional)
    this.processSpawnsMultidirectional(spawnBounds);

    // 2. Process dynamic spawners
    this.processSpawnersMultidirectional(spawnBounds, time);

    // 3. Despawn entities outside destroy zone (multidirectional)
    this.processDespawnsMultidirectional(despawnBounds);

    // 4. Culling (activate/deactivate by visibility)
    this.processCullingMultidirectional(cullBounds);
  }

  /**
   * Process pending spawns (multidirectional)
   * Spawns entities that are within the spawn bounds
   */
  private processSpawnsMultidirectional(bounds: DynamicBounds): void {
    for (let i = this.spawnIndex; i < this.pendingSpawns.length; i++) {
      const pending = this.pendingSpawns[i];

      // Get Y position for bounds check
      const y = pending.type === 'static'
        ? (pending.data as InstanceData).y
        : (pending.data as BossData).y;

      // Check if entity is within spawn bounds (multidirectional)
      if (!this.isInBounds(pending.x, y, bounds)) {
        // For sorted spawns, if X is beyond right bound we can stop
        // But we need to check other spawns in case of vertical levels
        if (pending.x > bounds.right) {
          break;
        }
        continue;
      }

      let entity: Entity | null = null;

      if (pending.type === 'static') {
        const instanceData = pending.data as InstanceData;
        entity = this.factory.createFromInstance(instanceData);
      } else if (pending.type === 'boss') {
        const bossData = pending.data as BossData;
        entity = this.factory.createFromInstance({
          entityId: bossData.entityId,
          x: bossData.x,
          y: bossData.y,
        });
        if (entity) {
          (entity as Entity & { isBoss: boolean }).isBoss = true;
        }
      }

      if (entity) {
        // Assign unique ID for tracking
        const spawnId = ++this.entityIdCounter;
        (entity as Entity & { _spawnId: number })._spawnId = spawnId;

        this.activeEntities.add(entity);
        entity.spawnData = pending;

        // Mark as just spawned to skip initial culling
        (entity as Entity & { _justSpawned?: boolean })._justSpawned = true;

        // Emit event for entity creation
        this.scene.events.emit('entitySpawned', entity);
      }

      this.spawnIndex = i + 1;
    }
  }

  /**
   * Process dynamic spawners (multidirectional)
   * Activates spawners when their trigger point enters the spawn bounds
   */
  private processSpawnersMultidirectional(bounds: DynamicBounds, time: number): void {
    // Check for new spawner activations
    for (let i = this.spawnerIndex; i < this.spawners.length; i++) {
      const spawner = this.spawners[i];
      const triggerY = spawner.spawnArea.y + spawner.spawnArea.height / 2;

      // Check if spawner trigger is within bounds (multidirectional)
      if (!this.isInBounds(spawner.triggerX, triggerY, bounds)) {
        if (spawner.triggerX > bounds.right) {
          break;
        }
        continue;
      }

      if (!spawner.triggered) {
        spawner.triggered = true;
        spawner.active = true;
        spawner.lastSpawnTime = time;
        spawner.spawnCount = 0;
        this.spawnerIndex = i + 1;

        if (this.debugEnabled) {
          console.log(`RuntimeManagerSpawn: Activated spawner at x=${spawner.triggerX}`);
        }
      }
    }

    // Process active spawners
    for (const spawner of this.spawners) {
      if (!spawner.active) continue;

      if (time >= spawner.lastSpawnTime + spawner.interval) {
        if (spawner.spawnCount < spawner.count) {
          // Spawn in random position within area
          const area = spawner.spawnArea;
          const x = area.x + Math.random() * area.width;
          const y = area.y + Math.random() * area.height;

          const entity = this.factory.createFromInstance({
            entityId: spawner.entityId,
            x,
            y,
          });

          if (entity) {
            this.activeEntities.add(entity);

            // Emit event for entity creation
            this.scene.events.emit('entitySpawned', entity);

            if (this.debugEnabled) {
              console.log(`[RuntimeManagerSpawn] SPAWNER: created entity at (${x.toFixed(0)}, ${y.toFixed(0)})`);
            }
          }

          spawner.lastSpawnTime = time;
          spawner.spawnCount++;
        } else {
          // Spawner finished
          spawner.active = false;

          if (this.debugEnabled) {
            console.log(`RuntimeManagerSpawn: Spawner completed at x=${spawner.triggerX}`);
          }
        }
      }
    }
  }

  /**
   * Despawn entities outside destroy zone (multidirectional)
   * Destroys entities that are outside the despawn bounds in ANY direction
   * Respects entity.spawnFlags.canDespawn
   */
  private processDespawnsMultidirectional(bounds: DynamicBounds): void {
    for (const entity of this.activeEntities) {
      // Don't despawn bosses
      if ((entity as Entity & { isBoss?: boolean }).isBoss) continue;

      // Skip if entity cannot be despawned (default: false)
      if (!entity.spawnFlags.canDespawn) continue;

      // Check if entity is OUTSIDE the despawn bounds (multidirectional)
      if (!this.isInBounds(entity.x, entity.y, bounds)) {
        entity.destroy();
        this.activeEntities.delete(entity);
      }
    }
  }

  /**
   * Culling: activate/deactivate entities by visibility (multidirectional)
   * Uses pre-calculated bounds with velocity-based expansion
   * Respects entity.spawnFlags.canCull and alwaysActive
   */
  private processCullingMultidirectional(bounds: DynamicBounds): void {
    for (const entity of this.activeEntities) {
      // Skip culling for entities that just spawned (let them update at least one frame)
      const justSpawned = (entity as Entity & { _justSpawned?: boolean })._justSpawned;
      if (justSpawned) {
        (entity as Entity & { _justSpawned?: boolean })._justSpawned = false;
        continue;
      }

      // Skip if entity is always active (ignores culling)
      if (entity.spawnFlags.alwaysActive) continue;

      // Skip if entity cannot be culled
      if (!entity.spawnFlags.canCull) continue;

      const inView = this.isInBounds(entity.x, entity.y, bounds);

      if (inView !== entity.active) {
        entity.setActive(inView);
        entity.setVisible(inView);

        if (inView) {
          entity.onEnterView();
        } else {
          entity.onExitView();
        }
      }
    }
  }

  /**
   * Notify that an entity has died (for cleanup)
   */
  onEntityDeath(entity: Entity): void {
    this.activeEntities.delete(entity);
  }

  /**
   * Spawn an entity dynamically at runtime (not from level data)
   * Used for boss-spawned enemies, drops, etc.
   */
  spawnEntityAt(entityId: string, x: number, y: number, extraData?: Record<string, unknown>): Entity | null {
    const entity = this.factory.createFromInstance({
      entityId,
      x,
      y,
      ...extraData,
    });

    if (entity) {
      // Assign unique ID for tracking
      const spawnId = ++this.entityIdCounter;
      (entity as Entity & { _spawnId: number })._spawnId = spawnId;

      this.activeEntities.add(entity);

      // Mark as dynamically spawned (won't be re-spawned if despawned)
      (entity as Entity & { _dynamicSpawn?: boolean })._dynamicSpawn = true;

      // Emit event for entity creation
      this.scene.events.emit('entitySpawned', entity);

      if (this.debugEnabled) {
        console.log(`[RuntimeManagerSpawn] Dynamic spawn: ${entityId} at (${x.toFixed(0)}, ${y.toFixed(0)})`);
      }
    }

    return entity;
  }

  /**
   * Get count of active entities
   */
  getActiveCount(): number {
    return this.activeEntities.size;
  }

  /**
   * Get count of pending spawns
   */
  getPendingCount(): number {
    return this.pendingSpawns.length - this.spawnIndex;
  }

  /**
   * Get count of active spawners
   */
  getActiveSpawnerCount(): number {
    return this.spawners.filter(s => s.active).length;
  }

  /**
   * Get all active entities
   */
  getActiveEntities(): ReadonlySet<Entity> {
    return this.activeEntities;
  }

  /**
   * Enable/disable debug logging
   */
  setDebug(enabled: boolean): void {
    this.debugEnabled = enabled;
  }

  /**
   * Get spawn statistics
   */
  getStats(): {
    active: number;
    pending: number;
    spawned: number;
    activeSpawners: number;
  } {
    return {
      active: this.activeEntities.size,
      pending: this.pendingSpawns.length - this.spawnIndex,
      spawned: this.spawnIndex,
      activeSpawners: this.spawners.filter(s => s.active).length,
    };
  }

  /**
   * Get current bounds for debug visualization
   * @param playerVelocity - Optional player velocity for dynamic buffer
   */
  getDebugBounds(playerVelocity?: Phaser.Math.Vector2): {
    cull: DynamicBounds;
    spawn: DynamicBounds;
    despawn: DynamicBounds;
  } {
    const camera = this.scene.cameras.main;
    return {
      cull: this.calculateBounds(camera, this.config.cullMargin, playerVelocity),
      spawn: this.calculateBounds(camera, this.config.spawnMargin, playerVelocity),
      despawn: this.calculateBounds(camera, this.config.despawnMargin),
    };
  }

  /**
   * Clear all state
   */
  clear(): void {
    // Destroy all active entities
    for (const entity of this.activeEntities) {
      entity.destroy();
    }
    this.activeEntities.clear();

    // Reset state
    this.pendingSpawns = [];
    this.spawners = [];
    this.spawnIndex = 0;
    this.spawnerIndex = 0;
  }

  /**
   * Update configuration
   */
  setConfig(config: Partial<SpawnConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): SpawnConfig {
    return { ...this.config };
  }
}
